<template>
  <template v-if="editPaymentInfo">
    <Button
      id="cancel-edit-payment-info"
      text
      class="mr-4"
      :disabled="validating || saving"
      :label="t('sites.singleSite.financialConfig.paymentInfo.actions.cancel')"
      @click.stop="cancelEditPaymentInfoMethod"
    />

    <Button
      id="save-edit-payment-info"
      :loading="saving"
      :disabled="validating || !isPaymentInfoValid"
      :label="primaryActionLabel"
      @click.stop="saveEditPaymentInfo"
    />
  </template>

  <Button
    v-else
    id="edit-payment-info"
    :label="editActionLabel"
    text
    @click.stop="editPaymentInfoMethod"
  >
    <template #icon>
      <i class="material-symbols-rounded text-[1.5rem] mr-2">edit</i>
    </template>
  </Button>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { SitesRouteNames } from '../../../../../../enums';
import { useFinancialConfigStore, usePaymentInfoStore } from '../../../../../../routes';
import { storeToRefs } from 'pinia';
import { useFormStore } from '@centric-os/stores/form/use-form-store';
import { useStoreAPIAction } from '@centric-os/stores/plugins/api';
import { useEventBus } from '@vueuse/core';
import { useRoute } from 'vue-router';
import { computed, inject } from 'vue';
import { formIdSymbol } from '@centric-os/types';
import { FinancialFormIDs } from '../../../enums/enums.js';
import { usePaymentInfo } from '../../composables';

// Set localization
const { t } = useI18n();

const paymentInfoStore = usePaymentInfoStore();
const { isPaymentInfoValid } = storeToRefs(paymentInfoStore);
const { loading: validating } = useStoreAPIAction(paymentInfoStore, 'validatePaymentInfo');
const { loading: saving } = useStoreAPIAction(paymentInfoStore, 'savePaymentInfo');

// Set financial config store
const fConfigStore = useFinancialConfigStore();
const { editPaymentInfo, isStationInitialSetup } = storeToRefs(fConfigStore);

// Set form store
const activeFormId: string = inject(formIdSymbol, FinancialFormIDs.PAYMENT_INFO_FORM);
const formStore = useFormStore();
const form = formStore.getForm(activeFormId);

const route = useRoute();

const { hasPaymentConfig } = usePaymentInfo();

// Set edit payment info method
const editPaymentInfoMethod = (): void => {
  editPaymentInfo.value = true;
};

const cancelEditPaymentInfoMethod = (): void => {
  if (!isStationInitialSetup.value || hasPaymentConfig.value) {
    editPaymentInfo.value = false; // For initial setup without inherited or payment config it needs to stay in edit state
  }
  if (isStationInitialSetup.value) {
    // @ts-expect-error FIXME: Argument of type 'string' is not assignable to parameter of type 'never'.
    form.value?.resetField('paymentConfig.paymentSettings.freedompay.freedompay_terminal_id');
    // @ts-expect-error FIXME: Argument of type 'string' is not assignable to parameter of type 'never'.
    form.value?.resetField('paymentConfig.paymentSettings.freedompay.freedompay_store_id');
    // @ts-expect-error FIXME: Argument of type 'string' is not assignable to parameter of type 'never'.
    form.value?.resetField('paymentConfig.paymentSettings.type');
  } else {
    form.value?.resetForm();
  }
};

// Set event bus for saving payment info
const isSiteConfig = route.name === SitesRouteNames.SITE_FINANCIAL_CONFIG;
const eventBusKey = `save-payment-info-${isSiteConfig ? 'site' : 'station'}`;
const { emit: saveEdit } = useEventBus<void>(eventBusKey);

// Save edit payment info
const saveEditPaymentInfo = (): void => {
  if (isStationInitialSetup.value) {
    editPaymentInfo.value = false;
    return;
  }
  saveEdit(); // Emits to PaymentInfo.vue
};

const editActionLabel = computed<string>(() => {
  if (isStationInitialSetup.value) {
    return t('sites.singleSite.financialConfig.paymentInfo.actions.editFreedomPay');
  }
  return t('sites.singleSite.financialConfig.paymentInfo.actions.edit');
});

const primaryActionLabel = computed<string>(() => {
  if (isStationInitialSetup.value) {
    return t('sites.singleSite.financialConfig.paymentInfo.actions.updateFreedomPay');
  }
  return t('sites.singleSite.financialConfig.paymentInfo.actions.save');
});
</script>
